import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Typography,
  Modal,
  Tabs,
  Popconfirm,
  Card,
} from "antd";
import type { InputRef } from "antd";
import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  SearchOutlined,
  MailOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  FileSearchOutlined,
  InboxOutlined,
  BellOutlined,
  NumberOutlined,
  IdcardOutlined,
  TagOutlined,
  EyeOutlined,
  BankOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  UserOutlined,
  SettingOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import debounce from "lodash.debounce";
import type { ColumnType } from "antd/es/table";
import {
  FETCH_ALL_REMINDERS,
  FETCH_SIGNED_DOCUMENTS,
  SEND_REMINDER_EMAIL,
} from "../../services/apiEndPoints";
import { IDatabase } from "../types/document.types";
import { imageInfo, snakeToCapitalCase } from "../upload/DualUpload";
import { getTagColor } from "./reminder.utils";
import { isPdf } from "../document/DocumentTableComponent";
import axios from "../../services/axiosInstance";
import { toastSuccess, toastError, toastWarning } from "../../utils/toast";

const { Title, Text } = Typography;

/* ─── Stat card config ─────────────────────────────────── */
const STAT_CARDS = [
  {
    key: "30",
    label: "Due in 30 Days",
    icon: <ExclamationCircleOutlined />,
    color: "#ef4444",
    bg: "linear-gradient(135deg, #fef2f2, #fee2e2)",
    border: "#fca5a5",
    iconColor: "#ef4444",
  },
  {
    key: "60",
    label: "Due in 31–60 Days",
    icon: <WarningOutlined />,
    color: "#f59e0b",
    bg: "linear-gradient(135deg, #fffbeb, #fef3c7)",
    border: "#fcd34d",
    iconColor: "#f59e0b",
  },
  {
    key: "rest",
    label: "Due Later",
    icon: <CalendarOutlined />,
    color: "#3b82f6",
    bg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
    border: "#93c5fd",
    iconColor: "#3b82f6",
  },
  {
    key: "expired",
    label: "Expired",
    icon: <ClockCircleOutlined />,
    color: "#6b7280",
    bg: "linear-gradient(135deg, #f9fafb, #f3f4f6)",
    border: "#d1d5db",
    iconColor: "#6b7280",
  },
  {
    key: "renewed",
    label: "Renewed",
    icon: <CheckCircleOutlined />,
    color: "#10b981",
    bg: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
    border: "#6ee7b7",
    iconColor: "#10b981",
  },
];

const ReminderComponent = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [data, setData] = useState<IDatabase[]>([]);
  const searchInput = useRef<InputRef>(null);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [emailLoadingId, setEmailLoadingId] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [activeTab, setActiveTab] = useState<string>("30");
  const [loading, setLoading] = useState(true);

  const today = useMemo(() => moment().startOf("day"), []);

  /* ---------------- API ---------------- */

  const fetchSignedUrl = async (s3Url: string) => {
    const response = await axios.get(
      `${FETCH_SIGNED_DOCUMENTS}?s3Url=${encodeURIComponent(s3Url)}`
    );
    if (response?.data?.results) return response.data.results;
    throw new Error("Failed to fetch signed URL");
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(FETCH_ALL_REMINDERS);
      if (response?.data?.results) setData(response.data.results);
    } finally {
      setLoading(false);
    }
  };

  const sendEmailReminder = async (id: string) => {
    try {
      setEmailLoadingId(id);
      await axios.post(SEND_REMINDER_EMAIL(id), {});
      toastSuccess("Reminder email sent successfully");
    } catch {
      toastError("Failed to send reminder email");
    } finally {
      setEmailLoadingId(null);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  /* ---------------- SEARCH ---------------- */

  const handleSearch = useMemo(
    () =>
      debounce((selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
      }, 300),
    []
  );

  const getColumnSearchProps = useCallback((
    dataIndex: keyof IDatabase
  ): ColumnType<IDatabase> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${String(dataIndex)}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8 }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters?.()}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes(String(value).toLowerCase()) ?? false,
    render: (text: string) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text || ""}
        />
      ) : (
        text
      ),
  }), [searchText, searchedColumn, handleSearch]);

  const handleBulkDownload = () => {
    const doc = new jsPDF("l", "mm", "a4");

    const tabMap: Record<string, IDatabase[]> = {
      "30": last30Days,
      "60": last60Days,
      rest: rest,
      expired: expired,
      renewed: renewed,
    };

    const source = tabMap[activeTab] || [];

    if (!source.length) {
      toastWarning("No records to download");
      return;
    }

    const titleMap: Record<string, string> = {
      "30": "Documents Expiring in 30 Days",
      "60": "Documents Expiring in 31–60 Days",
      rest: "Documents Expiring Later",
      expired: "Expired Documents",
      renewed: "Renewed Documents",
    };

    doc.setFontSize(16);
    doc.text(titleMap[activeTab], 14, 16);

    doc.setFontSize(10);
    doc.text(`Generated on: ${moment().format("MMM D, YYYY")}`, 14, 22);

    const tableData = source.map((item, index) => ({
      index: index + 1,
      uniqId: item.uniqId || "-",
      type: item.documentType || "-",
      company: item.companyName || "-",
      location: item.location || "-",
      expiry: item.expiryDate
        ? moment(item.expiryDate).format("MMM D, YYYY")
        : "-",
      status: item.isRenewed
        ? "Renewed"
        : item.expiryDate && moment(item.expiryDate).diff(today, "days") < 0
        ? "Expired"
        : "Active",
      details:
        typeof item.meta === "string"
          ? item.meta
          : item.meta && typeof item.meta === "object"
          ? Array.isArray(item.meta)
            ? item.meta.join(", ")
            : Object.entries(item.meta)
                .map(([key, value]) => `${snakeToCapitalCase(key)}: ${value}`)
                .join(", ")
          : "-",
    }));

    autoTable(doc, {
      startY: 28,
      head: [
        [
          "#",
          "Document No",
          "Type",
          "Company",
          "Location",
          "Expiry Date",
          "Status",
          "Details",
        ],
      ],
      body: tableData.map((row) => [
        row.index,
        row.uniqId,
        row.type,
        row.company,
        row.location,
        row.expiry,
        row.status,
        row.details || "-",
      ]),
      styles: { fontSize: 9, cellPadding: 3, overflow: "linebreak" },
      headStyles: {
        fillColor: [22, 119, 255],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      margin: { left: 14, right: 14 },
    });

    doc.save(`${titleMap[activeTab].replace(/\s+/g, "_")}.pdf`);
  };

  /* ---------------- COLUMNS ---------------- */

  const columns: ColumnType<IDatabase>[] = useMemo(
    () => [
      {
        title: <><NumberOutlined style={{ fontSize: 12 }} /> #</>,
        width: 60,
        align: "center",
        render: (_: any, __: any, index: number) => (
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
            {(pagination.current - 1) * pagination.pageSize + index + 1}
          </Text>
        ),
      },
      {
        title: <><IdcardOutlined style={{ fontSize: 12 }} /> Document No</>,
        dataIndex: "uniqId",
        key: "uniqId",
        width: 170,
        render: (value?: string) =>
          value ? (
            <Text strong style={{ fontSize: 13, color: "#1e293b" }}>
              {value}
            </Text>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: <><TagOutlined style={{ fontSize: 12 }} /> Type</>,
        dataIndex: "documentType",
        key: "documentType",
        sorter: (a: IDatabase, b: IDatabase) =>
          (a.documentType || "").localeCompare(b.documentType || ""),
        ...getColumnSearchProps("documentType"),
      },
      {
        title: <><CalendarOutlined style={{ fontSize: 12 }} /> Expiry Date</>,
        dataIndex: "expiryDate",
        key: "expiryDate",
        width: 160,
        align: "center" as const,
        render: (date: string) => {
          if (!date) return "-";
          const daysLeft = moment(date).diff(today, "days");

          let color: "green" | "orange" | "red" = "green";
          if (daysLeft <= 30) color = "red";
          else if (daysLeft <= 60) color = "orange";

          return (
            <Tag color={color} style={{ fontWeight: 500 }}>
              {moment(date).format("MMM D, YYYY")}
            </Tag>
          );
        },
      },
      {
        title: <><EyeOutlined style={{ fontSize: 12 }} /> File</>,
        dataIndex: "s3Url",
        key: "s3Url",
        width: 90,
        align: "center" as const,
        render: (s3Url: string) =>
          s3Url ? (
            <Button
              type="link"
              size="small"
              icon={<FileSearchOutlined />}
              onClick={async () => {
                try {
                  setLoadingPreview(true);
                  const url = await fetchSignedUrl(s3Url);
                  setSignedUrl(url);
                  setIsPreviewOpen(true);
                } catch {
                  toastError("Failed to load document");
                } finally {
                  setLoadingPreview(false);
                }
              }}
              style={{ fontSize: 12, padding: "0 4px" }}
            >
              View
            </Button>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: <><BankOutlined style={{ fontSize: 12 }} /> Company</>,
        dataIndex: "companyName",
        key: "companyName",
        sorter: (a: IDatabase, b: IDatabase) =>
          (a.companyName || "").localeCompare(b.companyName || ""),
        ...getColumnSearchProps("companyName"),
        render: (value?: string) =>
          value ? (
            <Space size={6}>
              <BankOutlined style={{ color: "#94a3b8", fontSize: 13 }} />
              <Text style={{ fontSize: 13 }}>{value}</Text>
            </Space>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: <><EnvironmentOutlined style={{ fontSize: 12 }} /> Location</>,
        dataIndex: "location",
        key: "location",
        sorter: (a: IDatabase, b: IDatabase) =>
          (a.location || "").localeCompare(b.location || ""),
        ...getColumnSearchProps("location"),
        render: (location?: string) =>
          location ? (
            <Space size={6}>
              <EnvironmentOutlined style={{ color: "#94a3b8", fontSize: 13 }} />
              <Text style={{ fontSize: 13 }}>{location}</Text>
            </Space>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: <><InfoCircleOutlined style={{ fontSize: 12 }} /> Details</>,
        dataIndex: "meta",
        key: "meta",
        width: 420,
        render: (meta: Record<string, any> | string[]) => {
          if (!meta) return "-";

          if (typeof meta === "object" && !Array.isArray(meta)) {
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {imageInfo(meta).map(({ key, value }) => (
                  <Tag key={key} color={getTagColor(key)} style={{ fontSize: 10 }}>
                    <b>{snakeToCapitalCase(key)}</b>: {String(value)}
                  </Tag>
                ))}
              </div>
            );
          }

          if (Array.isArray(meta)) {
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {meta.map((item) => (
                  <Tag key={item}>{item}</Tag>
                ))}
              </div>
            );
          }

          return "-";
        },
      },
      {
        title: <><UserOutlined style={{ fontSize: 12 }} /> Uploaded By</>,
        dataIndex: "uploadedBy",
        key: "uploadedBy",
        width: 150,
        ellipsis: true,
        render: (value?: string) =>
          value ? (
            <Text style={{ fontSize: 13 }}>{value}</Text>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: <><SettingOutlined style={{ fontSize: 12 }} /> Actions</>,
        key: "actions",
        width: 140,
        align: "center" as const,
        render: (_: any, record: IDatabase) => {
          const isRenewed = record.isRenewed;

          return (
            <Popconfirm
              title={
                isRenewed
                  ? "Already renewed"
                  : "Send reminder email?"
              }
              description={
                isRenewed
                  ? "This document has been renewed."
                  : "An email will be sent to the relevant contact."
              }
              onConfirm={() =>
                !isRenewed && sendEmailReminder(record.documentId)
              }
              disabled={isRenewed}
            >
              <Button
                icon={<MailOutlined />}
                type="primary"
                ghost
                size="small"
                disabled={isRenewed}
                loading={!isRenewed && emailLoadingId === record.documentId}
                style={{ borderRadius: 6, fontWeight: 500 }}
              >
                Remind
              </Button>
            </Popconfirm>
          );
        },
      },
    ],
    [emailLoadingId, pagination, today, getColumnSearchProps]
  );

  /* ---------------- BUCKETS ---------------- */

  const { last30Days, last60Days, rest, expired, renewed } = useMemo(() => {
    const expired: IDatabase[] = [];
    const last30Days: IDatabase[] = [];
    const last60Days: IDatabase[] = [];
    const rest: IDatabase[] = [];
    const renewed: IDatabase[] = [];

    data.forEach((item) => {
      if (item.isRenewed) {
        renewed.push(item);
        return;
      }

      if (!item.expiryDate) {
        rest.push(item);
        return;
      }

      const diff = moment(item.expiryDate).diff(today, "days");

      if (diff < 0) expired.push(item);
      else if (diff <= 30) last30Days.push(item);
      else if (diff <= 60) last60Days.push(item);
      else rest.push(item);
    });

    return { last30Days, last60Days, rest, expired, renewed };
  }, [data, today]);

  const bucketCounts: Record<string, number> = {
    "30": last30Days.length,
    "60": last60Days.length,
    rest: rest.length,
    expired: expired.length,
    renewed: renewed.length,
  };

  /* ---------------- UI ---------------- */

  const tableProps = {
    columns,
    size: "middle" as const,
  };

  const tabItems = [
    { key: "30", label: "Expiring in 30 Days", source: last30Days, icon: <ExclamationCircleOutlined /> },
    { key: "60", label: "Expiring 31–60 Days", source: last60Days, icon: <WarningOutlined /> },
    { key: "rest", label: "Expiring Later", source: rest, icon: <CalendarOutlined /> },
    { key: "expired", label: "Expired", source: expired, icon: <ClockCircleOutlined /> },
    { key: "renewed", label: "Renewed", source: renewed, icon: <CheckCircleOutlined /> },
  ];

  return (
    <div>
        {/* Page header */}
        <Row justify="space-between" align="middle" className="page-header-row">
          <Col>
            <Title level={4} className="page-title">
              <BellOutlined style={{ marginRight: 8, color: "#1677ff" }} />
              Expiry Reminders
            </Title>
            <Text className="page-subtitle">
              Track document expiry and send renewal reminders
            </Text>
          </Col>
        </Row>

        {/* Stat cards */}
        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
          {STAT_CARDS.map((card) => (
            <Col key={card.key} xs={24} sm={12} md={8} lg={6} xl={4} style={{ flex: 1 }}>
              <Card
                className="stat-card"
                style={{
                  background: card.bg,
                  border: `1px solid ${card.border}`,
                  cursor: "pointer",
                  opacity: activeTab === card.key ? 1 : 0.82,
                  transform: activeTab === card.key ? "translateY(-2px)" : undefined,
                  boxShadow:
                    activeTab === card.key
                      ? `0 6px 20px ${card.border}60`
                      : undefined,
                }}
                styles={{ body: { padding: "14px 16px" } }}
                onClick={() => setActiveTab(card.key)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: `${card.color}18`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      color: card.iconColor,
                      flexShrink: 0,
                    }}
                  >
                    {card.icon}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: card.color,
                        lineHeight: 1.1,
                      }}
                    >
                      {bucketCounts[card.key]}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#64748b",
                        fontWeight: 500,
                        lineHeight: 1.3,
                      }}
                    >
                      {card.label}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Tabs + Table */}
        <div className="content-card">
          <Tabs
            defaultActiveKey="30"
            size="middle"
            activeKey={activeTab}
            onChange={setActiveTab}
            style={{ padding: "0 16px" }}
            tabBarExtraContent={{
              right: (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleBulkDownload}
                  style={{ marginBottom: 4 }}
                >
                  Download PDF
                </Button>
              ),
            }}
            items={tabItems.map(({ key, label, source, icon }) => ({
              key,
              label: (
                <span>
                  {icon} {label}
                  <span
                    className="tab-badge"
                    style={{
                      background:
                        activeTab === key
                          ? "#1677ff"
                          : "#e2e8f0",
                      color: activeTab === key ? "#fff" : "#64748b",
                    }}
                  >
                    {source.length}
                  </span>
                </span>
              ),
              children: (
                <div style={{ padding: "0 0 4px" }}>
                  <Table
                    rowKey="documentId"
                    dataSource={source}
                    sticky
                    loading={{
                      spinning: loading,
                      tip: "Loading reminders...",
                    }}
                    locale={{
                      emptyText: (
                        <div style={{ padding: "40px 0" }}>
                          <InboxOutlined
                            style={{
                              fontSize: 42,
                              color: "#cbd5e1",
                              marginBottom: 12,
                            }}
                          />
                          <div
                            style={{
                              color: "#64748b",
                              fontSize: 14,
                              fontWeight: 500,
                            }}
                          >
                            No records in this category
                          </div>
                          <div
                            style={{
                              color: "#94a3b8",
                              fontSize: 13,
                              marginTop: 4,
                            }}
                          >
                            Documents matching this criteria will appear here
                          </div>
                        </div>
                      ),
                    }}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50"],
                      showTotal: (total, range) => (
                        <Text style={{ color: "#64748b", fontSize: 13 }}>
                          Showing{" "}
                          <Text strong>
                            {range[0]}-{range[1]}
                          </Text>{" "}
                          of <Text strong>{total}</Text> record
                          {total !== 1 ? "s" : ""}
                        </Text>
                      ),
                    }}
                    onChange={(paginationInfo) => {
                      setPagination({
                        current: paginationInfo.current || 1,
                        pageSize: paginationInfo.pageSize || 10,
                      });
                    }}
                    scroll={{ x: "max-content" }}
                    {...tableProps}
                  />
                </div>
              ),
            }))}
          />
        </div>

      <Modal
        open={isPreviewOpen}
        title={<><EyeOutlined style={{ marginRight: 8, color: "#1677ff" }} />Document Preview</>}
        onCancel={() => {
          setIsPreviewOpen(false);
          setSignedUrl(null);
        }}
        width="80%"
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            disabled={!signedUrl}
            onClick={() => {
              if (!signedUrl) return;
              const link = document.createElement("a");
              link.href = signedUrl;
              link.target = "_blank";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download
          </Button>,
          <Button key="close" icon={<CloseOutlined />} onClick={() => setIsPreviewOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {loadingPreview ? (
          <div style={{ textAlign: "center", padding: 40 }}>Loading...</div>
        ) : signedUrl && isPdf(signedUrl) ? (
          <iframe src={signedUrl} width="100%" style={{ height: "75vh" }} title="Document Preview" />
        ) : (
          <img
            src={signedUrl || ""}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "75vh",
              display: "block",
              margin: "0 auto",
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default ReminderComponent;
